import { createHookContext } from '@clover-util/react-context';
import { useCustomer } from 'hooks/context/customer/customer';
import { useDelivery } from 'hooks/context/Delivery';
import { useServiceType } from 'hooks/context/serviceType';
import { ServiceType } from 'hooks/context/serviceType/models';
import { useGetLatestAddress, useUpsertLatestAddress } from 'hooks/useApi/useAddress';
import { useDeliveryValidation } from 'hooks/useDeliveryValidation';
import { useSmartNavigate } from 'hooks/useSmartNavigate';
import { Address } from 'olo-service/models';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { DELIVERY_OPTIONS } from '../DeliveryAddressModal.const';

export const { DeliveryAddressFormProvider, useDeliveryAddressForm } = createHookContext('DeliveryAddressForm', () => {
  const { deliveryAddress, setDeliveryAddress } = useDelivery();
  const { typeMetadata, isDelivery, update: updateServiceType } = useServiceType();

  const { isLoggedIn } = useCustomer();
  const upsertAddress = useUpsertLatestAddress();
  const navigate = useSmartNavigate();

  const getDeliveryPreference = useMemo(() => {
    if (typeMetadata.isContactlessDelivery === undefined) return DELIVERY_OPTIONS[0];

    return typeMetadata.isContactlessDelivery ? 'contactless' : 'meetDoor';
  }, [typeMetadata.isContactlessDelivery]);

  const [deliveryOption, setDeliveryOption] = useState(getDeliveryPreference);
  const [makeAddressDefault, setMakeAddressDefault] = useState(true);

  const [mainAddress, setMainAddress] = useState<Address | undefined>(deliveryAddress);
  const [address2, setAddress2] = useState<string | undefined>(deliveryAddress?.address2 || '');

  const [deliveryInstructions, setDeliveryInstructions] = useState<string | undefined>(isDelivery ? typeMetadata.note : '');

  const validateDelivery = useDeliveryValidation();
  const serverDefaultAddress = useGetLatestAddress();

  const [isLoadingInitialState, setIsLoadingInitialState] = useState(false);

  // incase default address is still loading when opening modal
  useEffect(() => {
    if (isLoggedIn && !deliveryAddress && serverDefaultAddress.isLoading) {
      setIsLoadingInitialState(true);

      serverDefaultAddress
        .refetch() // doesnt actually make new call, awaits the in progress one
        .then((response) => {
          const address = response.data;
          if (address) {
            setDeliveryOption(address.preference || 'contactless');
            setDeliveryAddress(address);
            setMainAddress(address);
            setAddress2(address.address2 || '');
            setDeliveryInstructions(address.note || '');
          }
        })
        .finally(() => {
          setIsLoadingInitialState(false);
        });
    }
  }, [deliveryAddress, isLoggedIn, serverDefaultAddress, setDeliveryAddress]);

  const handleDeliveryEstimateSuccess = useCallback((onClose?: () => void) => {
    if (mainAddress == null) return;

    if (isLoggedIn && makeAddressDefault) {
      upsertAddress.mutate({
        ...mainAddress,
        address2: address2 ? address2 : undefined,
        preference: deliveryOption,
        note: deliveryInstructions
      });
    }
    const address = {
      ...mainAddress,
      address2: address2 || undefined,
    };
    const metadata = { isContactlessDelivery: deliveryOption === 'contactless', note: deliveryInstructions };

    validateDelivery.changeGlobalStateToDelivery(address, metadata);
    updateServiceType(ServiceType.DELIVERY, metadata);
    onClose?.();

  }, [mainAddress, isLoggedIn, makeAddressDefault, address2, deliveryOption, deliveryInstructions, validateDelivery, updateServiceType, upsertAddress]);

  const submit = useCallback(
    async (onClose?: () => void) => {
      if (mainAddress == null) return;

      const address = { ...mainAddress, address2: address2 || undefined };

      validateDelivery.mutate(address, {
        onSuccess: () => {
          handleDeliveryEstimateSuccess(onClose);
        },
        onError: (error) => {
          navigate('.address-error', { state: { mainAddress: address, error: (error as Error).message } });
        }
      });
    },
    [address2, handleDeliveryEstimateSuccess, mainAddress, navigate, validateDelivery]
  );

  return {
    isReviewing: !!deliveryAddress,
    deliveryOption,
    setDeliveryOption,
    makeAddressDefault,
    setMakeAddressDefault,
    mainAddress,
    setMainAddress,
    address2,
    setAddress2,
    deliveryInstructions,
    setDeliveryInstructions,
    submit,
    isPostDeliveriesEstimatePending: validateDelivery.isLoading,
    isLoadingInitialState,
  };
});
